import {useEffect, useRef, useState} from 'react';
import ReviewHeader from './header/ReviewHeader';
import {Box, useMediaQuery} from '@mui/material';
import ArrSplitPanel from '../../common/arr-split-panel/ArrSplitPanel';
import ReviewLeftContentPanel from './review-left-content-panel/ReviewLeftContentPanel';
import './ViewAllReview.scss';
import JobList from './job-list-layout/JobList';
import {useGetReviewJobGroups} from '../../../hooks/ai/review/useReview';
import {IAIReviewJobsExportRequest} from '../../../api/types/aiReview.type';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {hideToast} from '../../../redux/slice/aiReviewSlice';
import {IJobGroup} from './utils';

const collapsedWidth = 48;
const expandedWidth = 260;

export const ViewAllReview = () => {
  const isBelowBreakpoint = useMediaQuery('(max-width:800px)');
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [leftSectionMinWidth, setLeftSectionMinWidth] =
    useState<number>(expandedWidth);
  const [hasJobsForExport, setHasJobsForExport] = useState(false);
  const [jobExportPayload, setJobExportPayload] =
    useState<IAIReviewJobsExportRequest>({});
  const [isCreateJob, setIsCreateJob] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<IJobGroup | null>(null);

  const {
    data: reviewJobGroupsResponse,
    isError: isReviewJobGroupsError,
    isPending: isReviewJobGroupsPending,
    refetch: refetchReviewJobGroups,
  } = useGetReviewJobGroups();
  const {toast} = useSelector((state: RootState) => state.aiReview);
  const dispatch = useDispatch();

  useEffect(() => {
    setIsCollapsed(isBelowBreakpoint);
  }, [isBelowBreakpoint]);

  const leftPanelRef = useRef(null);

  useEffect(() => {
    if (!leftPanelRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.width > collapsedWidth) {
          setIsCollapsed(false);
        }

        if (entry.contentRect.width >= expandedWidth) {
          setLeftSectionMinWidth(expandedWidth);
        }
      }
    });

    resizeObserver.observe(leftPanelRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  const leftPanel = () => (
    <Box className="review-left-content" ref={leftPanelRef}>
      <ReviewLeftContentPanel
        isCollapsed={isCollapsed}
        onToggleCollapse={() => {
          setIsCollapsed((prev) => !prev);
          setLeftSectionMinWidth((prev) =>
            prev === collapsedWidth ? expandedWidth : collapsedWidth
          );
        }}
        reviewJobGroupsResponse={reviewJobGroupsResponse}
        selectedGroup={selectedGroup}
        setSelectedGroup={setSelectedGroup}
        hasGroupLoadError={isReviewJobGroupsError}
        isGroupListPending={isReviewJobGroupsPending}
        onRetryLoadGroups={refetchReviewJobGroups}
      />
    </Box>
  );

  const rightPanel = () => (
    <JobList
      reviewJobGroupsResponse={reviewJobGroupsResponse}
      onExportPayloadChange={setJobExportPayload}
      onHasJobsChange={setHasJobsForExport}
      setIsCreateJob={setIsCreateJob}
      selectedGroup={selectedGroup}
    />
  );

  return (
    <Box className="view-ai-review-container">
      <Box className="review-header">
        <ReviewHeader
          hasJobsForExport={hasJobsForExport}
          jobExportPayload={jobExportPayload}
          isCreateJob={isCreateJob}
          setIsCreateJob={setIsCreateJob}
        />
      </Box>
      <Box className={`review-content`}>
        <ArrSplitPanel
          leftPanel={leftPanel()}
          rightPanel={rightPanel()}
          maxLeftPanelWidth={leftSectionMinWidth}
          maxLeftPanelRatio={0.6}
        />
      </Box>
      {toast?.open && (
        <ArrToastNotification
          title={toast.title}
          message={toast.message}
          status={toast.status}
          onClose={() => dispatch(hideToast())}
          data-testid="ar-review-toast-global"
          aria-label={toast.title}
        />
      )}
    </Box>
  );
};

export default ViewAllReview;
